import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import EventModal from "./EventModal";
import EventDetailsModal from "./EventDetailsModal";
import SeriesActionModal from "./SeriesActionModal";
import RecurringScopeModal from "./RecurringScopeModal";
import CategoryManagerModal from "./CategoryManagerModal";
import { useEvents } from "../hooks/useEvents";
import { deleteAttachment, uploadAttachment } from "../api/attachmentsApi";
import { fetchCategories, createCategory, updateCategory, deleteCategory } from "../api/categoriesApi";
import { submitTimeEntry, updateTimeEntry, deleteTimeEntry } from "../api/timeEntryApi";
import { normalizeEventAttachments } from "../utils/eventAttachments";
import { getRepeatValidationError } from "../utils/repeatValidation";
import "./MobileDayGrid.css";

// ─── Layout constants ──────────────────────────────────────────────────────────
const SLOT_HEIGHT = 25;          // px per 30-minute slot
const HOUR_HEIGHT = SLOT_HEIGHT * 2; // 50 px per hour
const TOTAL_HEIGHT = HOUR_HEIGHT * 24; // 1200 px total
const TOTAL_SLOTS = 48;
const MINUTES_PER_SLOT = 30;
const PIXELS_PER_MINUTE = SLOT_HEIGHT / MINUTES_PER_SLOT;
const MINUTES_PER_DAY = 24 * 60;
const LONG_PRESS_MS = 520;
const LONG_PRESS_CANCEL_PX = 24;

// ─── Empty event template ──────────────────────────────────────────────────────
const EMPTY_EVENT = {
  create_request_id: "",
  title: "",
  start: "",
  end: "",
  repeat_rule: "none",
  repeat_until: "",
  all_day: false,
  is_lesson: false,
  no_show: false,
  fb_time_entry_id: "",
  project_id: null,
  project_name: "",
  invoice_notes: "",
  internal_notes: "",
  location: "",
  category_id: "",
  category_name: "",
  category_color: "",
  reminder_enabled: false,
  reminder_config: "",
  attachments: [],
  series_id: "",
};

const newCreateRequestId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

// ─── Pure helpers ──────────────────────────────────────────────────────────────
function pad(n) {
  return String(n).padStart(2, "0");
}

function toDateStr(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toDayStart(date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function toLocalISO(date) {
  return `${toDateStr(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function clampSlot(slot) {
  return Math.max(0, Math.min(TOTAL_SLOTS - 1, slot));
}

function clampMinutes(minutes) {
  return Math.max(0, Math.min(MINUTES_PER_DAY, minutes));
}

function minutesToGridPx(minutes) {
  return clampMinutes(minutes) * PIXELS_PER_MINUTE;
}

function isSameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function buildWeekDates(anchorDate) {
  const start = new Date(anchorDate);
  start.setDate(start.getDate() - start.getDay());
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function matchesSearch(event, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return true;
  const attachmentsText = Array.isArray(event.extendedProps?.attachments)
    ? event.extendedProps.attachments
        .map((item) => `${String(item?.label || "")} ${String(item?.file_name || "")} ${String(item?.url || "")}`)
        .join(" ")
    : "";
  const joined = [
    event.title,
    event.start,
    event.end,
    event.extendedProps?.location,
    event.extendedProps?.project_name,
    event.extendedProps?.invoice_notes,
    event.extendedProps?.internal_notes,
    event.extendedProps?.category_name,
    attachmentsText,
  ]
    .map((value) => String(value || "").toLowerCase())
    .join(" ");
  return joined.includes(q);
}

function isDateOnlyValue(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function parseDateOnlyLocal(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function parseEventBoundary(value) {
  const localDateOnly = parseDateOnlyLocal(value);
  if (localDateOnly) return localDateOnly;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function datePartFromValue(value) {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function isAllDayEvent(event) {
  if (event?.allDay === true) return true;
  if (event?.extendedProps?.all_day === true) return true;
  if (isDateOnlyValue(event?.start)) return true;
  return isDateOnlyValue(event?.start) && isDateOnlyValue(event?.end);
}

/** Filter events that overlap the given YYYY-MM-DD date string. */
function eventsForDate(events, dateStr) {
  const dayStart = parseDateOnlyLocal(dateStr);
  if (!dayStart) return [];
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  return events.filter((ev) => {
    if (!ev?.start) return false;

    if (isAllDayEvent(ev)) {
      const startPart = datePartFromValue(ev.start);
      if (!startPart) return false;

      const start = parseDateOnlyLocal(startPart);
      if (!start) return false;

      const endPart = datePartFromValue(ev.end);
      let endExclusive = endPart ? parseDateOnlyLocal(endPart) : null;
      if (!endExclusive || endExclusive <= start) {
        endExclusive = new Date(start);
        endExclusive.setDate(endExclusive.getDate() + 1);
      }

      return start < dayEnd && endExclusive > dayStart;
    }

    const start = parseEventBoundary(ev.start);
    if (!start) return false;

    let end = ev.end ? parseEventBoundary(ev.end) : null;
    if (!end || end <= start) {
      end = new Date(start.getTime() + 3_600_000);
    }

    return start < dayEnd && end > dayStart;
  });
}

/**
 * Assign non-overlapping columns to events using a greedy algorithm.
 * Returns [{ event, col, totalCols }].
 */
function calculateLayout(dayEvents) {
  const sorted = [...dayEvents].sort((a, b) => {
    const aStart = a.start ? new Date(a.start).getTime() : 0;
    const bStart = b.start ? new Date(b.start).getTime() : 0;
    if (aStart !== bStart) return aStart - bStart;
    const aEnd = a.end ? new Date(a.end).getTime() : aStart + 3_600_000;
    const bEnd = b.end ? new Date(b.end).getTime() : bStart + 3_600_000;
    return bEnd - aEnd; // longer events first
  });

  // columnEnds[i] = timestamp when column i next becomes free
  const columnEnds = [];
  const result = sorted.map((event) => {
    const evStart = event.start ? new Date(event.start).getTime() : 0;
    const evEnd = event.end ? new Date(event.end).getTime() : evStart + 3_600_000;
    let col = 0;
    while (columnEnds[col] !== undefined && columnEnds[col] > evStart) {
      col++;
    }
    columnEnds[col] = evEnd;
    return { event, col, totalCols: 1 /* filled below */ };
  });

  // Second pass: compute totalCols per overlap group
  return result.map((item) => {
    const evStart = item.event.start ? new Date(item.event.start).getTime() : 0;
    const evEnd = item.event.end ? new Date(item.event.end).getTime() : evStart + 3_600_000;
    let maxCol = 0;
    for (const other of result) {
      const oStart = other.event.start ? new Date(other.event.start).getTime() : 0;
      const oEnd = other.event.end ? new Date(other.event.end).getTime() : oStart + 3_600_000;
      if (oStart < evEnd && oEnd > evStart) {
        maxCol = Math.max(maxCol, other.col);
      }
    }
    return { ...item, totalCols: maxCol + 1 };
  });
}

// ─── Component ─────────────────────────────────────────────────────────────────
function MobileDayGrid({ selectedDate = null, searchTerm = "", onDateChange = null, extraEvents = [], createRequest = null, editRequest = null, timeFormat = "12h" }) {
  const {
    events,
    createSilently,
    update,
    updateSilently,
    remove,
    removeSeries,
    removeSeriesFrom,
    reload,
  } = useEvents();

  const [currentDate, setCurrentDate] = useState(() => {
    if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
      return toDayStart(selectedDate);
    }
    return toDayStart(new Date());
  });
  const [categories, setCategories] = useState([]);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  // EventModal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [draftEvent, setDraftEvent] = useState(null);
  const [isDrafting, setIsDrafting] = useState(false);

  // SeriesActionModal state
  const [seriesModalInfo, setSeriesModalInfo] = useState(null);

  // RecurringScopeModal — promise-based via ref
  const recurringScopeResolverRef = useRef(null);
  const [recurringScopeModal, setRecurringScopeModal] = useState(null); // { actionLabel }

  // Touch / swipe state
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const touchStartClientYRef = useRef(null); // for long-press slot calc
  const longPressTriggeredRef = useRef(false);
  const lastHandledCreateRequestRef = useRef(null);
  const lastHandledEditRequestRef = useRef(null);

  // DOM ref for the scrollable grid
  const gridScrollRef = useRef(null);

  // Live "now" indicator — updates every minute
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoryError("");
    try {
      const payload = await fetchCategories();
      setCategories(Array.isArray(payload) ? payload : []);
    } catch {
      setCategoryError("Could not load categories");
    }
  }, []);

  // Load categories once
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleCreateCategory = useCallback(async (payload) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await createCategory(payload);
      await loadCategories();
    } catch (err) {
      setCategoryError(err.message || "Could not create category");
    } finally {
      setCategorySaving(false);
    }
  }, [loadCategories]);

  const handleUpdateCategory = useCallback(async (categoryId, payload) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await updateCategory(categoryId, payload);

      const normalizedCategoryId = String(categoryId || "");
      const normalizedName = String(payload?.name || "").trim();
      const normalizedColor = String(payload?.color || "").trim();
      const targets = events.filter(
        (ev) => String(ev.extendedProps?.category_id || "") === normalizedCategoryId
      );

      if (targets.length > 0) {
        await Promise.all(
          targets.map((ev) =>
            updateSilently(ev.id, {
              extendedProps: {
                category_id: normalizedCategoryId,
                category_name: normalizedName,
                category_color: normalizedColor,
              },
            })
          )
        );
        await reload();
      }

      await loadCategories();

      setModalData((prev) => {
        if (!prev?.category_id || String(prev.category_id) !== normalizedCategoryId) return prev;
        return {
          ...prev,
          category_name: normalizedName || prev.category_name || "",
          category_color: normalizedColor || prev.category_color || "",
        };
      });
    } catch (err) {
      setCategoryError(err.message || "Could not update category");
    } finally {
      setCategorySaving(false);
    }
  }, [events, loadCategories, reload, updateSilently]);

  const handleDeleteCategory = useCallback(async (categoryId) => {
    setCategorySaving(true);
    setCategoryError("");
    try {
      await deleteCategory(categoryId);
      await loadCategories();

      const normalizedCategoryId = String(categoryId || "");
      setModalData((prev) => {
        if (!prev?.category_id || String(prev.category_id) !== normalizedCategoryId) return prev;
        return {
          ...prev,
          category_id: "",
          category_name: "",
          category_color: "",
        };
      });
    } catch (err) {
      setCategoryError(err.message || "Could not delete category");
    } finally {
      setCategorySaving(false);
    }
  }, [loadCategories]);

  // Scroll to 6 am whenever the displayed date changes
  useEffect(() => {
    if (!gridScrollRef.current) return;
    gridScrollRef.current.scrollTop = 6 * HOUR_HEIGHT;
  }, [currentDate]);

  useEffect(() => {
    if (!(selectedDate instanceof Date) || Number.isNaN(selectedDate.getTime())) return;
    setCurrentDate(toDayStart(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    if (typeof onDateChange === "function") {
      onDateChange(toDayStart(currentDate));
    }
  }, [currentDate, onDateChange]);

  // Clean up recurring-scope promise on unmount
  useEffect(() => {
    return () => {
      if (recurringScopeResolverRef.current) {
        recurringScopeResolverRef.current(null);
        recurringScopeResolverRef.current = null;
      }
    };
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────────
  const dateStr = toDateStr(currentDate);
  const dayEvents = useMemo(() => eventsForDate(events, dateStr), [events, dateStr]);
  const holidayDayEvents = useMemo(
    () => eventsForDate(extraEvents, dateStr),
    [dateStr, extraEvents]
  );
  const filteredDayEvents = useMemo(
    () => [...dayEvents.filter((event) => matchesSearch(event, searchTerm)), ...holidayDayEvents],
    [dayEvents, holidayDayEvents, searchTerm]
  );
  const timedEvents = useMemo(
    () => filteredDayEvents.filter((event) => !isAllDayEvent(event)),
    [filteredDayEvents]
  );
  const layout = useMemo(() => calculateLayout(timedEvents), [timedEvents]);

  // ── Navigation ────────────────────────────────────────────────────────────────
  const goToPrevDay = () => {
    setCurrentDate((d) => {
      const n = toDayStart(d);
      n.setDate(n.getDate() - 1);
      return n;
    });
  };

  const goToNextDay = () => {
    setCurrentDate((d) => {
      const n = toDayStart(d);
      n.setDate(n.getDate() + 1);
      return n;
    });
  };

  // ── Modal openers ─────────────────────────────────────────────────────────────
  const openNewEventAtDate = (baseDate, hour, minute, durationMinutes = 60) => {
    setDraftEvent(null);
    const startDate = new Date(baseDate);
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + durationMinutes);
    setModalData({
      ...EMPTY_EVENT,
      create_request_id: newCreateRequestId(),
      start: toLocalISO(startDate),
      end: toLocalISO(endDate),
    });
    setIsEdit(false);
    setModalOpen(true);
  };

  const openNewEvent = (hour, minute, durationMinutes = 60) => {
    openNewEventAtDate(currentDate, hour, minute, durationMinutes);
  };

  useEffect(() => {
    const requestToken = createRequest?.token;
    if (!requestToken) return;
    if (lastHandledCreateRequestRef.current === requestToken) return;
    lastHandledCreateRequestRef.current = requestToken;

    const baseDate = createRequest?.date instanceof Date && !Number.isNaN(createRequest.date.getTime())
      ? toDayStart(createRequest.date)
      : toDayStart(currentDate);

    setCurrentDate(baseDate);

    const nowDate = new Date();
    let startHour = 9;
    let startMinute = 0;
    if (isSameDay(baseDate, nowDate)) {
      if (nowDate.getMinutes() < 30) {
        startHour = nowDate.getHours();
        startMinute = 30;
      } else {
        startHour = Math.min(23, nowDate.getHours() + 1);
        startMinute = 0;
      }
    }

    openNewEventAtDate(baseDate, startHour, startMinute, 60);
  }, [createRequest, currentDate]);

  const openEditEvent = (event) => {
    setDetailsEvent(null);
    const isAllDay = isAllDayEvent(event);
    const allDayStart = datePartFromValue(event.start) || "";
    const allDayInclusiveEnd = (() => {
      const endPart = datePartFromValue(event.end);
      if (!endPart) return allDayStart;

      const endExclusive = parseDateOnlyLocal(endPart);
      if (!endExclusive) return allDayStart;

      const inclusive = new Date(endExclusive);
      inclusive.setDate(inclusive.getDate() - 1);
      return toDateStr(inclusive);
    })();

    setModalData({
      id: event.id,
      title: event.title || "",
      create_request_id: event.extendedProps?.create_request_id || "",
      all_day: isAllDay,
      start: isAllDay ? allDayStart : event.start || "",
      end: isAllDay ? allDayInclusiveEnd : event.end || "",
      repeat_rule:
        event.extendedProps?.repeat_rule || (event.extendedProps?.series_id ? "daily" : "none"),
      repeat_until: event.extendedProps?.repeat_until || "",
      is_lesson: event.extendedProps?.is_lesson ?? false,
      no_show: event.extendedProps?.no_show ?? false,
      fb_time_entry_id: event.extendedProps?.fb_time_entry_id ?? "",
      project_id: event.extendedProps?.project_id ?? null,
      project_name: event.extendedProps?.project_name ?? "",
      invoice_notes: event.extendedProps?.invoice_notes ?? "",
      internal_notes: event.extendedProps?.internal_notes ?? "",
      location: event.extendedProps?.location ?? "",
      category_id: event.extendedProps?.category_id ?? "",
      category_name: event.extendedProps?.category_name ?? "",
      category_color: event.extendedProps?.category_color ?? "",
      reminder_enabled: event.extendedProps?.reminder_enabled ?? false,
      reminder_config: event.extendedProps?.reminder_config ?? "",
      attachments: normalizeEventAttachments(event.extendedProps?.attachments),
      series_id: event.extendedProps?.series_id ?? "",
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  useEffect(() => {
    const requestToken = editRequest?.token;
    if (!requestToken) return;
    if (lastHandledEditRequestRef.current === requestToken) return;
    if (!editRequest?.event) return;
    lastHandledEditRequestRef.current = requestToken;

    const eventStart = new Date(editRequest.event.start || currentDate);
    const baseDate = Number.isNaN(eventStart.getTime()) ? toDayStart(currentDate) : toDayStart(eventStart);
    setCurrentDate(baseDate);
    openEditEvent(editRequest.event);
  }, [editRequest, currentDate]);

  const handleEventTap = (event) => {
    if (event?.extendedProps?.isHoliday === true) return;
    setDetailsEvent({
      ...event,
      extendedProps: {
        ...(event?.extendedProps || {}),
        attachments: normalizeEventAttachments(
          event?.extendedProps?.attachments || event?.attachments
        ),
      },
    });
  };

  const showDraftEvent = (hour, minute) => {
    const anchorSlot = clampSlot(Math.floor(((hour * 60) + minute) / 30));
    const startSlot = anchorSlot;
    const endSlotExclusive = Math.min(TOTAL_SLOTS, startSlot + 2); // default 1 hour

    const startDate = new Date(currentDate);
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + (endSlotExclusive - startSlot) * 30);

    const top = startSlot * SLOT_HEIGHT;
    const height = (endSlotExclusive - startSlot) * SLOT_HEIGHT;
    const timeLabel = `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}–${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

    setDraftEvent({ anchorSlot, startSlot, endSlotExclusive, top, height, timeLabel });
  };

  const resizeDraftEventFromTouch = (touchClientY) => {
    setDraftEvent((prev) => {
      if (!prev) return prev;
      const gridEl = gridScrollRef.current;
      if (!gridEl) return prev;

      const rect = gridEl.getBoundingClientRect();
      const relativeY = (touchClientY - rect.top) + gridEl.scrollTop;
      const touchedSlot = clampSlot(Math.floor(relativeY / SLOT_HEIGHT));

      let startSlot = prev.anchorSlot;
      let endSlotExclusive = Math.min(TOTAL_SLOTS, prev.anchorSlot + 2);

      if (touchedSlot >= prev.anchorSlot) {
        endSlotExclusive = Math.min(TOTAL_SLOTS, touchedSlot + 2);
      } else {
        startSlot = touchedSlot;
      }

      if (endSlotExclusive <= startSlot) {
        endSlotExclusive = Math.min(TOTAL_SLOTS, startSlot + 1);
      }

      const startDate = new Date(currentDate);
      const startMinutes = startSlot * 30;
      startDate.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + (endSlotExclusive - startSlot) * 30);

      return {
        ...prev,
        startSlot,
        endSlotExclusive,
        top: startSlot * SLOT_HEIGHT,
        height: (endSlotExclusive - startSlot) * SLOT_HEIGHT,
        timeLabel: `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}–${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`,
      };
    });
  };

  const handleEventEditRequest = () => {
    if (!detailsEvent) return;
    const seriesId = detailsEvent.extendedProps?.series_id;
    if (seriesId) {
      setSeriesModalInfo({ event: detailsEvent, seriesId });
      setDetailsEvent(null);
      return;
    }
    openEditEvent(detailsEvent);
  };

  const handleDetailsDelete = async () => {
    if (!detailsEvent) return;
    const fbId = detailsEvent.extendedProps?.fb_time_entry_id || "";
    if (fbId) {
      try {
        await deleteTimeEntry(fbId);
      } catch (err) {
        window.alert(`Could not delete Freshbooks entry:\n${err.message}`);
      }
    }
    await remove(detailsEvent.id);
    setDetailsEvent(null);
  };

  const handleDetailsReminderChange = async ({ enabled, config }) => {
    if (!detailsEvent) return;
    const payload = {
      title: detailsEvent.title,
      start: detailsEvent.start,
      end: detailsEvent.end,
      extendedProps: {
        ...(detailsEvent.extendedProps || {}),
        reminder_enabled: enabled === true,
        reminder_config: String(config || ""),
      },
    };

    try {
      await updateSilently(detailsEvent.id, payload);
      setDetailsEvent((prev) => {
        if (!prev || prev.id !== detailsEvent.id) return prev;
        return {
          ...prev,
          extendedProps: {
            ...(prev.extendedProps || {}),
            reminder_enabled: enabled === true,
            reminder_config: String(config || ""),
          },
        };
      });
    } catch (err) {
      window.alert(`Could not update reminder:\n${err.message}`);
    }
  };

  // ── Touch / swipe ─────────────────────────────────────────────────────────────
  const handleGridTouchStart = (e) => {
    if (modalOpen || detailsEvent) return;
    const touch = e.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchStartClientYRef.current = touch.clientY;

    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      const gridEl = gridScrollRef.current;
      if (!gridEl) return;
      const rect = gridEl.getBoundingClientRect();
      const relativeY =
        (touchStartClientYRef.current - rect.top) + gridEl.scrollTop;
      const slotIndex = Math.round(relativeY / SLOT_HEIGHT);
      const totalMinutes = slotIndex * 30;
      const hour = Math.min(Math.floor(totalMinutes / 60), 23);
      const minute = totalMinutes % 60;
      longPressTriggeredRef.current = true;
      setIsDrafting(true);
      showDraftEvent(hour, minute);
    }, LONG_PRESS_MS);
  };

  const handleGridTouchMove = (e) => {
    if (modalOpen || detailsEvent) return;
    if (longPressTriggeredRef.current && draftEvent) {
      e.preventDefault();
      const touch = e.touches[0];
      resizeDraftEventFromTouch(touch.clientY);
      return;
    }
    if (!longPressTimerRef.current) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartXRef.current);
    const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
    // Allow normal finger drift; cancel only if the gesture clearly became a scroll/swipe.
    if (deltaX > LONG_PRESS_CANCEL_PX || deltaY > LONG_PRESS_CANCEL_PX) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      setDraftEvent(null);
    }
  };

  const handleGridTouchEnd = (e) => {
    if (modalOpen || detailsEvent) return;
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (touchStartXRef.current === null) return;

    if (longPressTriggeredRef.current && draftEvent) {
      const startMinutes = draftEvent.startSlot * 30;
      const hour = Math.floor(startMinutes / 60);
      const minute = startMinutes % 60;
      const durationMinutes = Math.max(30, (draftEvent.endSlotExclusive - draftEvent.startSlot) * 30);
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      touchStartClientYRef.current = null;
      longPressTriggeredRef.current = false;
      setIsDrafting(false);
      window.setTimeout(() => {
        openNewEvent(hour, minute, durationMinutes);
      }, 80);
      return;
    }

    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;

    // Swipe: more horizontal than vertical, and at least 60px
    if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0) goToNextDay();
      else goToPrevDay();
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchStartClientYRef.current = null;
    longPressTriggeredRef.current = false;
    setIsDrafting(false);
  };

  const handleGridTouchCancel = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setDraftEvent(null);
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchStartClientYRef.current = null;
    longPressTriggeredRef.current = false;
    setIsDrafting(false);
  };

  // ── Recurring scope helpers ───────────────────────────────────────────────────
  const requestRecurringScope = useCallback((actionLabel) => {
    return new Promise((resolve) => {
      recurringScopeResolverRef.current = resolve;
      setRecurringScopeModal({ actionLabel });
    });
  }, []);

  const resolveRecurringScope = useCallback((value) => {
    const resolve = recurringScopeResolverRef.current;
    recurringScopeResolverRef.current = null;
    setRecurringScopeModal(null);
    if (resolve) resolve(value);
  }, []);

  // ── Freshbooks cleanup helper ─────────────────────────────────────────────────
  const deleteFreshbooksEntriesForEvents = async (targetEvents) => {
    const failures = [];
    for (const ev of targetEvents) {
      const fbId = ev?.extendedProps?.fb_time_entry_id;
      if (!fbId) continue;
      try {
        await deleteTimeEntry(fbId);
      } catch (err) {
        failures.push(`${ev.title || ev.id}: ${err.message}`);
      }
    }
    if (failures.length > 0) {
      window.alert(
        `Some Freshbooks entries could not be deleted:\n${failures.slice(0, 3).join("\n")}`
      );
    }
  };

  const getRecurrenceMetadata = (props) => ({
    series_id: props?.series_id || "",
    repeat_rule: props?.repeat_rule || "none",
    repeat_until: props?.repeat_until || "",
    repeat_interval: Number(props?.repeat_interval || 1) || 1,
    repeat_end_mode: props?.repeat_end_mode || "frequency",
    repeat_periods: Number(props?.repeat_periods || 3) || 3,
  });

  // ── Build recurring payloads from modal state (shared by create & update) ────
  const buildRecurringPayloadsFromModal = useCallback((modalInput, basePayload) => {
    const repeatRule = modalInput.repeat_rule || "none";
    const repeatInterval = Math.max(1, Number(modalInput.repeat_interval || 1));
    const repeatEndMode =
      modalInput.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatPeriods = Math.max(1, Number(modalInput.repeat_periods || 3));

    if (repeatRule === "none") return [basePayload];

    const pad2 = (n) => String(n).padStart(2, "0");
    const toDateOnly = (d) =>
      `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const toDateTimeSeconds = (d) =>
      `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T` +
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

    const addMonthsClamped = (date, months, anchorDay) => {
      const shifted = new Date(date);
      shifted.setDate(1);
      shifted.setMonth(shifted.getMonth() + months);
      const monthLastDay = new Date(
        shifted.getFullYear(), shifted.getMonth() + 1, 0
      ).getDate();
      shifted.setDate(Math.min(anchorDay, monthLastDay));
      return shifted;
    };

    const untilDate =
      repeatEndMode === "forever" || !String(modalInput.repeat_until || "").trim()
        ? null
        : new Date(`${modalInput.repeat_until}T23:59:59`);

    // For all-day, use local tz to avoid offset issues
    const startBase = modalInput.all_day
      ? (() => {
        const [sy, sm, sd] = basePayload.start.split("-").map(Number);
        return new Date(sy, sm - 1, sd);
      })()
      : new Date(basePayload.start);
    const endBase = modalInput.all_day
      ? (() => {
        const [ey, em, ed] = basePayload.end.split("-").map(Number);
        return new Date(ey, em - 1, ed);
      })()
      : new Date(basePayload.end);

    const anchorStartDay = startBase.getDate();
    const anchorEndDay = endBase.getDate();
    const anchorWeekday = startBase.getDay();
    const anchorNthWeekday = Math.floor((startBase.getDate() - 1) / 7) + 1;

    const monthlyNthWeekday = (date, nth, weekday) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const dayCount = new Date(year, month + 1, 0).getDate();
      const matches = [];
      for (let day = 1; day <= dayCount; day += 1) {
        if (new Date(year, month, day).getDay() === weekday) {
          matches.push(day);
        }
      }
      const idx = Math.max(0, Math.min(matches.length - 1, nth - 1));
      const chosenDay = matches[idx] || 1;
      return new Date(year, month, chosenDay);
    };

    const advanceCursor = (startDate, endDate) => {
      if (repeatRule === "weekly" || repeatRule === "weekly_specific_day" || repeatRule === "weekly_edge_first" || repeatRule === "weekly_edge_last") {
        startDate.setDate(startDate.getDate() + 7 * repeatInterval);
        endDate.setDate(endDate.getDate() + 7 * repeatInterval);
        return;
      }
      if (repeatRule === "monthly" || repeatRule === "monthly_specific_day") {
        const nextStart = addMonthsClamped(startDate, repeatInterval, anchorStartDay);
        const nextEnd = addMonthsClamped(endDate, repeatInterval, anchorEndDay);
        startDate.setTime(nextStart.getTime());
        endDate.setTime(nextEnd.getTime());
        return;
      }
      if (repeatRule === "monthly_edge_first") {
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() + repeatInterval);
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }
      if (repeatRule === "monthly_edge_last") {
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() + repeatInterval + 1);
        startDate.setDate(0);
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }
      if (repeatRule === "monthly_nth_weekday") {
        const monthCursor = new Date(startDate);
        monthCursor.setDate(1);
        monthCursor.setMonth(monthCursor.getMonth() + repeatInterval);
        const nextStart = monthlyNthWeekday(monthCursor, anchorNthWeekday, anchorWeekday);
        startDate.setTime(nextStart.getTime());
        const nextEnd = new Date(startDate.getTime() + (endBase.getTime() - startBase.getTime()));
        endDate.setTime(nextEnd.getTime());
        return;
      }
      if (repeatRule === "yearly" || repeatRule === "yearly_date") {
        startDate.setFullYear(startDate.getFullYear() + repeatInterval);
        endDate.setFullYear(endDate.getFullYear() + repeatInterval);
        return;
      }
      startDate.setDate(startDate.getDate() + repeatInterval);
      endDate.setDate(endDate.getDate() + repeatInterval);
    };

    const getForeverWindowCount = () => {
      if (repeatRule === "yearly" || repeatRule === "yearly_date") return 25;
      if (String(repeatRule).startsWith("monthly_")) return 36;
      if (String(repeatRule).startsWith("weekly_")) return 78;
      return 120;
    };

    if (
      Number.isNaN(startBase.getTime()) ||
      Number.isNaN(endBase.getTime()) ||
      (untilDate && Number.isNaN(untilDate.getTime()))
    ) {
      return [basePayload];
    }

    const recurringPayloads = [];
    const cursorStart = new Date(startBase);
    const cursorEnd = new Date(endBase);
    const maxOccurrences =
      repeatEndMode === "until"
        ? 260
        : repeatEndMode === "forever"
        ? getForeverWindowCount()
        : repeatPeriods;

    for (let i = 0; i < maxOccurrences; i++) {
      if (untilDate && cursorStart > untilDate) break;
      recurringPayloads.push({
        ...basePayload,
        start: modalInput.all_day ? toDateOnly(cursorStart) : toDateTimeSeconds(cursorStart),
        end: modalInput.all_day ? toDateOnly(cursorEnd) : toDateTimeSeconds(cursorEnd),
      });
      advanceCursor(cursorStart, cursorEnd);
    }

    return recurringPayloads.length > 0 ? recurringPayloads : [basePayload];
  }, []);

  // ── Validate times ────────────────────────────────────────────────────────────
  const validateTimes = () => {
    if (modalData.all_day) return !!modalData.start;
    if (!modalData.start || !modalData.end) return false;
    return new Date(modalData.start) < new Date(modalData.end);
  };

  const syncEventAttachments = async ({
    eventId,
    basePayload,
    currentAttachments = [],
    previousAttachments = [],
    pendingFiles = [],
  }) => {
    if (!eventId) return normalizeEventAttachments(currentAttachments);

    const normalizedCurrent = normalizeEventAttachments(currentAttachments);
    const normalizedPrevious = normalizeEventAttachments(previousAttachments);
    const currentStoragePaths = new Set(
      normalizedCurrent.map((item) => item.storage_path).filter(Boolean)
    );

    for (const item of normalizedPrevious) {
      if (!item.storage_path || currentStoragePaths.has(item.storage_path)) continue;
      try {
        await deleteAttachment(item.storage_path);
      } catch (err) {
        window.alert(`Event saved but attachment removal failed:\n${err.message || err}`);
      }
    }

    if (!Array.isArray(pendingFiles) || pendingFiles.length === 0) {
      return normalizedCurrent;
    }

    const uploadedAttachments = [];
    for (const file of pendingFiles) {
      try {
        uploadedAttachments.push(await uploadAttachment(file));
      } catch (err) {
        window.alert(`Event saved but attachment upload failed:\n${err.message || err}`);
      }
    }

    if (uploadedAttachments.length === 0) {
      return normalizedCurrent;
    }

    const nextAttachments = normalizeEventAttachments([
      ...normalizedCurrent,
      ...uploadedAttachments,
    ]);

    try {
      await updateSilently(eventId, {
        ...basePayload,
        extendedProps: {
          ...(basePayload.extendedProps || {}),
          attachments: nextAttachments,
        },
      });
    } catch (err) {
      window.alert(`Event saved but attachments could not be linked:\n${err.message || err}`);
      return normalizedCurrent;
    }

    return nextAttachments;
  };

  // ── handleSave — create new event (possibly recurring) ───────────────────────
  const handleSave = async (locationOverride = null, pendingFiles = []) => {
    if (isSaving) return;
    if (!validateTimes()) return;
    setIsSaving(true);

    try {

    const repeatRule = modalData.repeat_rule || "none";
    const repeatEndMode =
      modalData.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatInterval = Math.max(1, Number(modalData.repeat_interval || 1));
    const repeatPeriods = Math.max(1, Number(modalData.repeat_periods || 3));
    const repeatValidationError = getRepeatValidationError({
      repeatRule,
      repeatEndMode,
      repeatUntil: modalData.repeat_until,
      start: modalData.start,
    });
    if (repeatValidationError) {
      setIsSaving(false);
      window.alert(repeatValidationError);
      return;
    }

    const isLesson = modalData.is_lesson === true;
    const notes = modalData.no_show
      ? `[No Show] ${modalData.invoice_notes || ""}`.trim()
      : modalData.invoice_notes || "";

    const selectedCategory = categories.find((c) => c.id === (modalData.category_id || ""));
    const resolvedCategoryName = modalData.category_id
      ? selectedCategory?.name || modalData.category_name || ""
      : "";
    const resolvedCategoryColor = modalData.category_id
      ? selectedCategory?.color || modalData.category_color || ""
      : "";

    let payloadStart, payloadEnd;
    if (modalData.all_day) {
      payloadStart = modalData.start;
      const d = new Date(modalData.end + "T00:00:00");
      d.setDate(d.getDate() + 1);
      payloadEnd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } else {
      payloadStart = modalData.start.length === 16 ? `${modalData.start}:00` : modalData.start;
      payloadEnd = modalData.end.length === 16 ? `${modalData.end}:00` : modalData.end;
    }

    const seriesId = repeatRule !== "none" ? crypto.randomUUID() : "";
    const resolvedLocation =
      typeof locationOverride === "string" ? locationOverride : modalData.location || "";

    const resolvedAttachments = normalizeEventAttachments(modalData.attachments);

    const payload = {
      title: modalData.title,
      start: payloadStart,
      end: payloadEnd,
      extendedProps: {
        project_id: modalData.project_id ? String(modalData.project_id) : "",
        project_name: modalData.project_name || "",
        is_lesson: isLesson,
        no_show: modalData.no_show === true,
        fb_time_entry_id: "",
        invoice_notes: modalData.invoice_notes || "",
        internal_notes: modalData.internal_notes || "",
        location: resolvedLocation,
        category_id: modalData.category_id || "",
        category_name: resolvedCategoryName,
        category_color: resolvedCategoryColor,
        reminder_enabled: modalData.reminder_enabled === true,
        reminder_config: modalData.reminder_config || "",
        attachments: resolvedAttachments,
        series_id: seriesId,
        repeat_rule: repeatRule,
        repeat_until: modalData.repeat_until || "",
        repeat_interval: repeatInterval,
        repeat_periods: repeatPeriods,
        repeat_end_mode: repeatEndMode,
      },
    };

    const payloadsToCreate = buildRecurringPayloadsFromModal(modalData, payload);

    const baseCreateRequestId = String(modalData.create_request_id || newCreateRequestId());
    for (let index = 0; index < payloadsToCreate.length; index += 1) {
      const occurrencePayload = payloadsToCreate[index];
      const occurrenceCreateRequestId = `${baseCreateRequestId}:${index}`;
      occurrencePayload.extendedProps = {
        ...(occurrencePayload.extendedProps || {}),
        create_request_id: occurrenceCreateRequestId,
      };
      const newEvent = await createSilently(occurrencePayload);
      if (newEvent?.id) {
        await syncEventAttachments({
          eventId: newEvent.id,
          basePayload: occurrencePayload,
          currentAttachments: resolvedAttachments,
          previousAttachments: [],
          pendingFiles,
        });
      }
      if (!modalData.all_day && isLesson && newEvent?.id) {
        try {
          await submitTimeEntry({
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: occurrencePayload.start,
            eventEnd: occurrencePayload.end,
            notes,
            eventId: newEvent.id,
          });
        } catch (err) {
          window.alert(`Lesson saved but Freshbooks sync failed:\n${err.message}`);
        }
      }
    }

    await reload();
    setModalOpen(false);
    setModalData({});
    } catch (err) {
      window.alert(`Could not save event:\n${err?.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── handleUpdate — update existing event ─────────────────────────────────────
  const handleUpdate = async (locationOverride = null, pendingFiles = []) => {
    if (isSaving) return;
    if (!validateTimes()) return;

    const repeatRule = modalData.repeat_rule || "none";
    const repeatEndMode =
      modalData.repeat_end_mode || (repeatRule === "forever" ? "forever" : "frequency");
    const repeatValidationError = getRepeatValidationError({
      repeatRule,
      repeatEndMode,
      repeatUntil: modalData.repeat_until,
      start: modalData.start,
    });
    if (repeatValidationError) {
      window.alert(repeatValidationError);
      return;
    }

    setIsSaving(true);

    try {

    const isLesson = modalData.is_lesson === true;
    const fbId = modalData.fb_time_entry_id || "";
    const notes = modalData.no_show
      ? `[No Show] ${modalData.invoice_notes || ""}`.trim()
      : modalData.invoice_notes || "";

    const selectedCategory = categories.find((c) => c.id === (modalData.category_id || ""));
    const resolvedCategoryName = modalData.category_id
      ? selectedCategory?.name || modalData.category_name || ""
      : "";
    const resolvedCategoryColor = modalData.category_id
      ? selectedCategory?.color || modalData.category_color || ""
      : "";

    let fbTimeEntryId = fbId;
    if (!isLesson && fbId) {
      try {
        await deleteTimeEntry(fbId);
        fbTimeEntryId = "";
      } catch (err) {
        window.alert(`Could not delete Freshbooks entry:\n${err.message}`);
      }
    }

    let payloadStart, payloadEnd;
    if (modalData.all_day) {
      payloadStart = modalData.start;
      const [y, m, day] = modalData.end.split("-").map(Number);
      const nextDay = new Date(y, m - 1, day + 1);
      const ny = nextDay.getFullYear();
      const nm = String(nextDay.getMonth() + 1).padStart(2, "0");
      const nd = String(nextDay.getDate()).padStart(2, "0");
      payloadEnd = `${ny}-${nm}-${nd}`;
    } else {
      payloadStart = modalData.start.length === 16 ? `${modalData.start}:00` : modalData.start;
      payloadEnd = modalData.end.length === 16 ? `${modalData.end}:00` : modalData.end;
    }

    const originalEvent = events.find((ev) => ev.id === modalData.id);
    const originalRecurring = getRecurrenceMetadata(originalEvent?.extendedProps);
    const resolvedRepeatRule =
      modalData.repeat_rule === undefined ||
      modalData.repeat_rule === null ||
      modalData.repeat_rule === ""
        ? originalRecurring.repeat_rule || "none"
        : modalData.repeat_rule;
    const resolvedRepeatEndMode =
      modalData.repeat_end_mode ||
      originalRecurring.repeat_end_mode ||
      (resolvedRepeatRule === "forever" ? "forever" : "frequency");
    const resolvedRepeatUntil =
      resolvedRepeatRule === "forever" || resolvedRepeatEndMode === "forever"
        ? ""
        : modalData.repeat_until === undefined || modalData.repeat_until === null
        ? originalRecurring.repeat_until
        : modalData.repeat_until;
    const resolvedReminderConfig =
      modalData.reminder_enabled === true
        ? modalData.reminder_config || originalEvent?.extendedProps?.reminder_config || ""
        : "";
    const resolvedRepeatInterval =
      resolvedRepeatRule === "none"
        ? 1
        : Math.max(
            1,
            Number(
              modalData.repeat_interval === undefined || modalData.repeat_interval === null
                ? originalRecurring.repeat_interval
                : modalData.repeat_interval
            ) || 1
          );
    const resolvedRepeatPeriods = Math.max(
      1,
      Number(modalData.repeat_periods ?? originalRecurring.repeat_periods ?? 3) || 3
    );
    const updatedSeriesId =
      resolvedRepeatRule === "none"
        ? ""
        : modalData.series_id || originalRecurring.series_id || crypto.randomUUID();

    const resolvedLocation =
      typeof locationOverride === "string" ? locationOverride : modalData.location || "";

    const resolvedAttachments = normalizeEventAttachments(modalData.attachments);
    const originalAttachments = normalizeEventAttachments(originalEvent?.extendedProps?.attachments);

    const payload = {
      title: modalData.title,
      start: payloadStart,
      end: payloadEnd,
      extendedProps: {
        project_id: modalData.project_id ? String(modalData.project_id) : "",
        project_name: modalData.project_name || "",
        is_lesson: isLesson,
        no_show: modalData.no_show === true,
        fb_time_entry_id: fbTimeEntryId,
        invoice_notes: modalData.invoice_notes || "",
        internal_notes: modalData.internal_notes || "",
        location: resolvedLocation,
        category_id: modalData.category_id || "",
        category_name: resolvedCategoryName,
        category_color: resolvedCategoryColor,
        reminder_enabled: modalData.reminder_enabled === true,
        reminder_config: resolvedReminderConfig,
        attachments: resolvedAttachments,
        series_id: updatedSeriesId,
        repeat_rule: resolvedRepeatRule || "none",
        repeat_until: resolvedRepeatUntil || "",
        repeat_interval: resolvedRepeatInterval,
        repeat_periods: resolvedRepeatPeriods,
        repeat_end_mode: resolvedRepeatEndMode,
      },
    };

    const pad2 = (n) => String(n).padStart(2, "0");
    const toDateOnly = (value) => {
      const d = value instanceof Date ? value : new Date(value);
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    };

    const originalStart = originalEvent
      ? new Date(originalEvent.start)
      : new Date(payloadStart);
    const currentStart = new Date(payloadStart);
    const currentEnd = new Date(payloadEnd);
    const startShiftMs = currentStart.getTime() - originalStart.getTime();
    const updatedDurationMs = currentEnd.getTime() - currentStart.getTime();

    const applySharedFieldsToEvent = (eventItem) => {
      const eventStart = new Date(eventItem.start);
      const shiftedStart = new Date(eventStart.getTime() + startShiftMs);
      const shiftedEnd = new Date(shiftedStart.getTime() + updatedDurationMs);
      const targetStart = modalData.all_day
        ? toDateOnly(shiftedStart)
        : shiftedStart.toISOString();
      const targetEnd = modalData.all_day
        ? toDateOnly(shiftedEnd)
        : shiftedEnd.toISOString();
      return {
        title: modalData.title,
        start: targetStart,
        end: targetEnd,
        extendedProps: {
          project_id: modalData.project_id ? String(modalData.project_id) : "",
          project_name: modalData.project_name || "",
          is_lesson: isLesson,
          no_show: modalData.no_show === true,
          fb_time_entry_id: eventItem.extendedProps?.fb_time_entry_id || "",
          invoice_notes: modalData.invoice_notes || "",
          internal_notes: modalData.internal_notes || "",
          location: resolvedLocation,
          category_id: modalData.category_id || "",
          category_name: resolvedCategoryName,
          category_color: resolvedCategoryColor,
          reminder_enabled: modalData.reminder_enabled === true,
          reminder_config: resolvedReminderConfig,
          attachments: resolvedAttachments,
          series_id: updatedSeriesId,
          repeat_rule: resolvedRepeatRule || "none",
          repeat_until: resolvedRepeatUntil || "",
          repeat_interval: resolvedRepeatInterval,
          repeat_periods: resolvedRepeatPeriods,
          repeat_end_mode: resolvedRepeatEndMode,
        },
      };
    };

    const scope = modalData.series_id
      ? await requestRecurringScope("update")
      : "this";

    if (scope === null) {
      setIsSaving(false);
      return;
    }

    const recurrenceChanged =
      resolvedRepeatRule !== (originalRecurring.repeat_rule || "none") ||
      resolvedRepeatUntil !== (originalRecurring.repeat_until || "") ||
      resolvedRepeatInterval !== (originalRecurring.repeat_interval || 1) ||
      resolvedRepeatEndMode !== (originalRecurring.repeat_end_mode || "frequency") ||
      resolvedRepeatPeriods !== (originalRecurring.repeat_periods || 3);

    // Turning on recurrence for a one-off event (no series_id yet) must also generate
    // the future occurrences, not just flag the existing single event as recurring.
    const isPromotingToSeries = !modalData.series_id && resolvedRepeatRule !== "none";

    if ((modalData.series_id && recurrenceChanged && scope !== "this") || isPromotingToSeries) {
      const anchorDate = String(payload.start || modalData.start || "").slice(0, 10);

      if (isPromotingToSeries) {
        // The original single event is superseded by the newly generated series occurrences.
        await remove(modalData.id);
      } else if (scope === "all") {
        await removeSeries(modalData.series_id);
      } else {
        await removeSeriesFrom(modalData.series_id, anchorDate);
      }

      const regeneratedPayloads = buildRecurringPayloadsFromModal(modalData, payload).filter((item) => {
        if (isPromotingToSeries || scope === "all") return true;
        return String(item.start || "").slice(0, 10) >= anchorDate;
      });

      let firstCreatedId = null;
      for (let index = 0; index < regeneratedPayloads.length; index += 1) {
        const occurrencePayload = regeneratedPayloads[index];
        const newEvent = await createSilently(occurrencePayload);
        if (!firstCreatedId && newEvent?.id) {
          firstCreatedId = newEvent.id;
        }

        if (newEvent?.id) {
          await syncEventAttachments({
            eventId: newEvent.id,
            basePayload: occurrencePayload,
            currentAttachments: resolvedAttachments,
            previousAttachments: [],
            // Pending (not-yet-uploaded) files only apply to the occurrence being edited.
            pendingFiles: index === 0 ? pendingFiles : [],
          });
        }

        if (!modalData.all_day && isLesson && newEvent?.id) {
          try {
            await submitTimeEntry({
              projectId: modalData.project_id,
              eventTitle: modalData.title,
              eventStart: occurrencePayload.start,
              eventEnd: occurrencePayload.end,
              notes,
              eventId: newEvent.id,
            });
          } catch (err) {
            window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
          }
        }
      }

      await reload();
      setModalOpen(false);
      setModalData({});
      return;
    }

    await update(modalData.id, payload);
    await syncEventAttachments({
      eventId: modalData.id,
      basePayload: payload,
      currentAttachments: resolvedAttachments,
      previousAttachments: originalAttachments,
      pendingFiles,
    });

    if (scope === "following" && modalData.series_id) {
      const anchorDate = String(modalData.start || "").slice(0, 10);
      const following = events.filter(
        (ev) =>
          ev.id !== modalData.id &&
          ev.extendedProps?.series_id === modalData.series_id &&
          String(ev.start || "").slice(0, 10) >= anchorDate
      );
      for (const ev of following) {
        const targetPayload = applySharedFieldsToEvent(ev);
        await updateSilently(ev.id, targetPayload);
        const targetFbId = ev.extendedProps?.fb_time_entry_id || "";
        if (!modalData.all_day && isLesson && targetFbId) {
          try {
            await updateTimeEntry(targetFbId, {
              projectId: modalData.project_id,
              eventTitle: modalData.title,
              eventStart: targetPayload.start,
              eventEnd: targetPayload.end,
              notes,
            });
          } catch (err) {
            window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
          }
        }
      }
    }

    if (!modalData.all_day && isLesson) {
      if (fbTimeEntryId) {
        try {
          await updateTimeEntry(fbTimeEntryId, {
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: payload.start,
            eventEnd: payload.end,
            notes,
          });
        } catch (err) {
          window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
        }
      } else {
        try {
          await submitTimeEntry({
            projectId: modalData.project_id,
            eventTitle: modalData.title,
            eventStart: payload.start,
            eventEnd: payload.end,
            notes,
            eventId: modalData.id,
          });
          reload();
        } catch (err) {
          window.alert(`Event saved but Freshbooks sync failed:\n${err.message}`);
        }
      }
    }

    await reload();
    setModalOpen(false);
    setModalData({});
    } catch (err) {
      window.alert(`Could not update event:\n${err?.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (modalData.fb_time_entry_id) {
      try {
        await deleteTimeEntry(modalData.fb_time_entry_id);
      } catch (err) {
        window.alert(`Could not delete Freshbooks entry:\n${err.message}`);
      }
    }
    await remove(modalData.id);
    setModalOpen(false);
    setModalData({});
  };

  const handleModalDeleteFollowing = async () => {
    if (!modalData.series_id) return;
    const fromDate = String(modalData.start || "").slice(0, 10);
    const targets = events.filter(
      (ev) =>
        ev.extendedProps?.series_id === modalData.series_id &&
        String(ev.start || "").slice(0, 10) >= fromDate
    );
    await deleteFreshbooksEntriesForEvents(targets);
    await removeSeriesFrom(modalData.series_id, fromDate);
    setModalOpen(false);
    setModalData({});
  };

  const handleModalDeleteAll = async () => {
    if (!modalData.series_id) return;
    const targets = events.filter(
      (ev) => ev.extendedProps?.series_id === modalData.series_id
    );
    await deleteFreshbooksEntriesForEvents(targets);
    await removeSeries(modalData.series_id);
    setModalOpen(false);
    setModalData({});
  };

  const handleModalSave = (locationOverride = null, pendingFiles = []) => {
    if (isEdit) handleUpdate(locationOverride, pendingFiles);
    else handleSave(locationOverride, pendingFiles);
  };

  // ── Series action modal handlers ──────────────────────────────────────────────
  const handleSeriesEditThis = () => {
    const { event } = seriesModalInfo;
    setSeriesModalInfo(null);
    openEditEvent(event);
  };

  const handleSeriesDeleteThis = async () => {
    const { event } = seriesModalInfo;
    setSeriesModalInfo(null);
    await deleteFreshbooksEntriesForEvents([event]);
    await remove(event.id);
  };

  const handleSeriesDeleteFollowing = async () => {
    const { event, seriesId } = seriesModalInfo;
    const fromDate = String(event.start || "").slice(0, 10);
    const targets = events.filter(
      (ev) =>
        ev.extendedProps?.series_id === seriesId &&
        String(ev.start || "").slice(0, 10) >= fromDate
    );
    setSeriesModalInfo(null);
    await deleteFreshbooksEntriesForEvents(targets);
    await removeSeriesFrom(seriesId, fromDate);
  };

  const handleSeriesDeleteAll = async () => {
    const { seriesId } = seriesModalInfo;
    const targets = events.filter(
      (ev) => ev.extendedProps?.series_id === seriesId
    );
    setSeriesModalInfo(null);
    await deleteFreshbooksEntriesForEvents(targets);
    await removeSeries(seriesId);
  };

  // ── Render helpers ────────────────────────────────────────────────────────────
  const renderEvents = () => {
    return layout.map(({ event, col, totalCols }) => {
      const startMs = event.start ? new Date(event.start).getTime() : 0;
      const endMs = event.end
        ? new Date(event.end).getTime()
        : startMs + 3_600_000;
      const startDate = new Date(startMs);
      const endDate = new Date(endMs);
      const startMins = startDate.getHours() * 60 + startDate.getMinutes();
      const durationMins = Math.max(30, (endMs - startMs) / 60_000);

      const top = minutesToGridPx(startMins);
      const height = Math.max(SLOT_HEIGHT, minutesToGridPx(durationMins));
      const colWidth = 100 / totalCols;
      const leftPct = col * colWidth;

      const categoryColor = String(
        event.extendedProps?.category_color || ""
      ).trim();
      const hasColor = /^#[0-9A-Fa-f]{6}$/.test(categoryColor);
      const bgColor = hasColor ? `${categoryColor}33` : undefined;
      const borderColor = hasColor ? categoryColor : undefined;

      const timeLabel =
        `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}` +
        `–${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

      const classNames = [
        "mdg-event",
        event.extendedProps?.is_lesson ? "mdg-event--lesson" : "",
        event.extendedProps?.no_show ? "mdg-event--noshow" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <div
          key={event.id}
          className={classNames}
          style={{
            top: `${top}px`,
            height: `${height}px`,
            left: `calc(${leftPct}% + 2px)`,
            width: `calc(${colWidth}% - 4px)`,
            ...(hasColor
              ? { backgroundColor: bgColor, borderLeftColor: borderColor }
              : {}),
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleEventTap(event);
          }}
          // Prevent the grid's long-press from firing when tapping an event
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => {
            e.stopPropagation();
            handleEventTap(event);
          }}
        >
          <div className="mdg-event-title">{event.title}</div>
          {height >= SLOT_HEIGHT * 1.5 && (
            <div className="mdg-event-time">{timeLabel}</div>
          )}
          {height >= SLOT_HEIGHT * 2 && event.extendedProps?.location && (
            <div className="mdg-event-location">
              {event.extendedProps.location}
            </div>
          )}
        </div>
      );
    });
  };

  const renderNowIndicator = () => {
    if (!isSameDay(currentDate, now)) return null;
    const mins = now.getHours() * 60 + now.getMinutes();
    const top = minutesToGridPx(mins);
    const timeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return (
      <div className="mdg-now-indicator" style={{ top: `${top}px` }}>
        <div className="mdg-now-dot" />
        <div className="mdg-now-line" />
        <div className="mdg-now-time">{timeStr}</div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  const weekDates = buildWeekDates(currentDate);
  const today = new Date();

  // All-day events for this date (date-only start strings)
  const allDayEvents = filteredDayEvents.filter((ev) => isAllDayEvent(ev));

  const renderDraftEvent = () => {
    if (!draftEvent) return null;
    return (
      <div
        className="mdg-event mdg-event--draft"
        style={{
          top: `${draftEvent.top}px`,
          height: `${draftEvent.height}px`,
          left: "calc(0% + 2px)",
          width: "calc(100% - 4px)",
        }}
      >
        <div className="mdg-event-title">New event</div>
        <div className="mdg-event-time">{draftEvent.timeLabel}</div>
      </div>
    );
  };
  return (
    <div className="mdg-wrapper">
      {/* ── Weekday/date strip ── */}
      <div className="mdg-week-strip" aria-label="Week navigation">
        {weekDates.map((date) => {
          const isSelected = isSameDay(date, currentDate);
          const isTodayCell = isSameDay(date, today);
          const weekday = date.toLocaleDateString("en-US", { weekday: "short" }).charAt(0);
          return (
            <button
              key={toDateStr(date)}
              type="button"
              className={`mdg-weekday${isSelected ? " mdg-weekday--selected" : ""}`}
              onClick={() => setCurrentDate(toDayStart(date))}
              aria-label={`Open ${toDateStr(date)}`}
            >
              <span className="mdg-weekday-name">{weekday}</span>
              <span className={`mdg-weekday-num${isTodayCell ? " mdg-weekday-num--today" : ""}`}>
                {date.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── All-day strip ── */}
      {allDayEvents.length > 0 && (
        <div className="mdg-allday-strip">
          {allDayEvents.map((ev) => {
            const classNames = [
              "mdg-allday-chip",
              ev.extendedProps?.is_lesson ? "mdg-allday-chip--lesson" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <div
                key={ev.id}
                className={classNames}
                onClick={() => handleEventTap(ev)}
              >
                {ev.title}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Scrollable time grid ── */}
      <div
        className={`mdg-grid-scroll${isDrafting ? " mdg-grid-scroll--drafting" : ""}`}
        ref={gridScrollRef}
        onTouchStart={handleGridTouchStart}
        onTouchMove={handleGridTouchMove}
        onTouchEnd={handleGridTouchEnd}
        onTouchCancel={handleGridTouchCancel}
      >
        <div
          className="mdg-grid-inner"
          style={{ height: `${TOTAL_HEIGHT}px` }}
        >
          {/* Time labels (left column) */}
          <div className="mdg-time-labels" aria-hidden="true">
            {Array.from({ length: 24 }, (_, hour) => (
              <div
                key={hour}
                className="mdg-time-label-row"
                style={{ top: `${hour * HOUR_HEIGHT}px` }}
              >
                {hour === 0 ? null : (
                  <span className="mdg-time-label">{`${pad(hour)}:00`}</span>
                )}
              </div>
            ))}
          </div>

          {/* Horizontal slot dividers */}
          <div className="mdg-slots" aria-hidden="true">
            {Array.from({ length: 48 }, (_, i) => (
              <div
                key={i}
                className={`mdg-slot${i % 2 === 1 ? " mdg-slot--hour" : ""}`}
              />
            ))}
          </div>

          {/* Event blocks + now indicator */}
          <div className="mdg-events-layer">
            {renderDraftEvent()}
            {renderEvents()}
            {renderNowIndicator()}
          </div>
        </div>
      </div>

      {/* ── EventModal ── */}
      <EventModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalData({});
          setDraftEvent(null);
        }}
        data={modalData}
        setData={setModalData}
        isEdit={isEdit}
        onSave={handleModalSave}
        onDelete={isEdit ? handleDelete : undefined}
        onDeleteFollowing={isEdit ? handleModalDeleteFollowing : undefined}
        onDeleteAll={isEdit ? handleModalDeleteAll : undefined}
        isSaving={isSaving}
        categories={categories}
        timeFormat={timeFormat}
        onOpenCategoryManager={() => setCategoryModalOpen(true)}
      />

      <CategoryManagerModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categories={categories}
        onCreate={handleCreateCategory}
        onUpdate={handleUpdateCategory}
        onDelete={handleDeleteCategory}
        isSaving={categorySaving}
        error={categoryError}
      />

      <EventDetailsModal
        event={detailsEvent}
        onClose={() => setDetailsEvent(null)}
        onEdit={handleEventEditRequest}
        onDelete={handleDetailsDelete}
        onReminderChange={handleDetailsReminderChange}
        timeFormat={timeFormat}
        onManageCategories={() => {
          setDetailsEvent(null);
          setCategoryModalOpen(true);
        }}
      />

      {/* ── SeriesActionModal ── */}
      {seriesModalInfo && (
        <SeriesActionModal
          eventTitle={seriesModalInfo.event.title}
          onEditThis={handleSeriesEditThis}
          onDeleteThis={handleSeriesDeleteThis}
          onDeleteFollowing={handleSeriesDeleteFollowing}
          onDeleteAll={handleSeriesDeleteAll}
          onClose={() => setSeriesModalInfo(null)}
        />
      )}

      {/* ── RecurringScopeModal ── */}
      {recurringScopeModal && (
        <RecurringScopeModal
          actionLabel={recurringScopeModal.actionLabel}
          onThisOnly={() => resolveRecurringScope("this")}
          onThisAndFollowing={() => resolveRecurringScope("following")}
          onCancel={() => resolveRecurringScope(null)}
        />
      )}
    </div>
  );
}

export default MobileDayGrid;
